/**
 * Routing — who is told about which alert, on which cameras, and how. A site's notifications
 * (escalation policies), on the site's Alerts page and on the cloud's page for that site (same
 * component; the cloud's API proxies to the site).
 *
 * The split: an alert RULE decides whether something is an alert. ROUTING decides who hears about
 * it, from which cameras, and how — each person on their own channels: Dashboard pop-up on their
 * screen, email, WhatsApp. The delivery CHANNELS tab only sets up how email / WhatsApp are sent.
 *
 *   alert board     which rules' alerts show on the live alert board (for everyone who can see
 *                   the camera) — the rule's websocket_enabled, set here
 *   contact groups  "Safety team: Asha — pop-up, email, WhatsApp · Ravi — WhatsApp": picked as one
 *                   in any notification and any escalation step
 *   list            each notification as a sentence: "When <alert> happens on <cameras>, tell
 *                   <people> by <channels>. If nobody acknowledges: after 5 min tell …".
 *                   "Everything else" (the kind's default) last
 *   editor          name → which alerts → which cameras → who and how → if nobody acknowledges,
 *                   with the sentence on top and the escalation drawn below
 *   check           "Who would be told?" for an example alert, step by step
 *
 * Every matching notification applies (a person in two is told once, on every channel asked for);
 * "Everything else" only when nothing else matched or the match reaches nobody. A user only gets
 * alerts from cameras they can see. Acknowledging the alert stops the "if nobody acknowledges" steps.
 */
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  ArrowPathIcon,
  BellAlertIcon,
  ChatBubbleLeftRightIcon,
  ClockIcon,
  ComputerDesktopIcon,
  DocumentDuplicateIcon,
  EnvelopeIcon,
  MagnifyingGlassIcon,
  ShieldCheckIcon,
  PencilSquareIcon,
  PlusIcon,
  TrashIcon,
  UserGroupIcon,
  UserIcon,
  UserPlusIcon,
  UsersIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { createPortal } from 'react-dom';
import Modal from '../common/Modal';
import ConfirmDialog from '../common/ConfirmDialog';
import { Callout } from '../common/Callout';
import { AASwitch, aaInputCls, aaLabelCls } from './AlertActionsControls';
import ChangeHistory from './ChangeHistory';
import {
  CHANNEL_WORD,
  SYSTEM_ALERT_KINDS,
  WATCHLIST_ROUTING_CATEGORIES,
  CLOUD_PROBLEMS,
  alertActionsErrorText,
  emptyPolicyLevel,
  type ContactGroup,
  type PolicyChannels,
  type PolicyDirectory,
  type PolicyKind,
  type PolicyLevel,
  type PolicyPerson,
  type PolicyPreview,
  type RoutingPoliciesApi,
  type RoutingPolicy,
} from './alertActionsModel';

export interface RoutingPoliciesProps {
  api: RoutingPoliciesApi;
  canEdit: boolean;
  /** 'site' on the site's page; 'central' when the cloud shows one site. */
  perspective?: 'site' | 'central';
  /** How an AI event type reads (each app has its own labels). */
  eventTypeLabel?: (type: string) => string;
  /** Above the list. */
  header?: ReactNode;
  /**
   * 'alerts' (a site's alerts — the default) or 'offline': Cloud (platform)'s cards for what the
   * cloud raises itself — a site going offline (its SITES ride in `cameras`, the directory lists
   * sites there) and the cloud server's own problems (`alerts.cloud`).
   */
  variant?: 'alerts' | 'offline';
  /** Where Contact groups / History go — the host's tab row. Without it they sit above the lists. */
  toolbarTarget?: HTMLElement | null;
}

const MAX_LEVELS = 5;
/** A small number box ("after [15] minutes") — aaInputCls is full width. */
const numCls = 'w-20 rounded-lg border border-border bg-background px-2 py-1 text-[13px] text-foreground disabled:opacity-50';
const SEVERITY_TEXT: Record<string, string> = { warning: 'warnings and critical', critical: 'critical only' };
const CHANNEL_KEYS: (keyof PolicyChannels)[] = ['dashboard', 'email', 'message_api'];
const listText = (items: string[], max = 4) => {
  if (!items.length) return '';
  const shown = items.slice(0, max);
  const more = items.length - shown.length;
  if (more > 0) return `${shown.join(', ')} and ${more} more`;
  return shown.length > 1 ? `${shown.slice(0, -1).join(', ')} and ${shown[shown.length - 1]}` : shown[0];
};
const CHANNEL_ICON = { dashboard: ComputerDesktopIcon, email: EnvelopeIcon, message_api: ChatBubbleLeftRightIcon };
const CHANNEL_CHIP = { dashboard: 'Pop-up', email: 'Email', message_api: 'WhatsApp' };
const CHANNEL_HINT = { dashboard: 'A pop-up on their screen in TRINETRA', email: 'An email', message_api: 'A WhatsApp / SMS message' };
const channelsText = (ch?: PolicyChannels) => listText(CHANNEL_KEYS.filter((k) => ch?.[k]).map((k) => CHANNEL_WORD[k])) || 'no way picked';

// ─── The words ──────────────────────────────────────────────────────

interface Names {
  user: Map<string, string>;
  role: Map<string, string>;
  camera: Map<string, string>;
  group: Map<string, string>;
  rule: Map<string, string>;
  contactGroup: Map<string, ContactGroup>;
}

function personLabel(p: PolicyPerson, names: Names): string {
  if (p.kind === 'user') return names.user.get(p.id || '') || 'a removed user';
  if (p.kind === 'role') return roleLabel(p.id, names).replace(/^Everyone/, 'everyone');
  if (p.kind === 'group') return names.contactGroup.get(p.id || '')?.name || 'a removed group';
  return p.name || p.email || p.phone || 'someone';
}

/**
 * "Asha and Ravi by pop-up and email" when everyone is told the same way, else
 * "Asha (pop-up, email), Ravi (WhatsApp)"; a group reads "Safety team (as set in the group)".
 */
function peopleText(people: PolicyPerson[], names: Names): string {
  if (!people.length) return '';
  const groups = people.filter((p) => p.kind === 'group').map((p) => `${personLabel(p, names)} (each as set in the group)`);
  const rest = people.filter((p) => p.kind !== 'group');
  const sigs = new Set(rest.map((p) => channelsText(p.channels)));
  const others = !rest.length ? ''
    : sigs.size === 1 ? `${listText(rest.map((p) => personLabel(p, names)), 4)} by ${[...sigs][0]}`
      : listText(rest.map((p) => `${personLabel(p, names)} (${channelsText(p.channels)})`), 4);
  return listText([others, ...groups].filter(Boolean), 6);
}

/** The cloud's "everyone with access to the site" (Site offline cards) — not a role of its own. */
const SITE_USERS = 'site_users';
function roleLabel(id: string | undefined, names: Names) {
  if (id === SITE_USERS) return 'Everyone with access to the site';
  return `Everyone with the ${names.role.get(id || '') || 'removed'} role`;
}

/** What a default catches, in words: one default for both (an older site has one per kind). */
const defaultWhat = (p: RoutingPolicy) => (p.kind === 'ai' ? 'AI alert' : p.kind === 'system' ? 'system problem' : p.kind === 'offline' ? 'site offline or cloud server problem' : 'alert or system problem');
/** "" for the one default; " — AI alerts" on an older site's per-kind default. */
const defaultSuffix = (p: RoutingPolicy) => (p.kind === 'ai' ? 'AI alerts' : p.kind === 'system' ? 'system problems' : '');

/** Covers camera alerts (AI and/or watchlist) / system problems ('both' covers each) / a site going offline. */
const coversAi = (p: RoutingPolicy) => p.kind === 'ai' || p.kind === 'both';
const coversSystem = (p: RoutingPolicy) => p.kind === 'system' || p.kind === 'both';
const isOffline = (p: RoutingPolicy) => p.kind === 'offline';
/** Cloud (platform) cards: a site going offline picked (absent = yes) / the cloud server's own problems ([] = every one). */
const pickedOffline = (p: RoutingPolicy) => isOffline(p) && p.alerts.offline !== false;
const pickedCloud = (p: RoutingPolicy) => isOffline(p) && Array.isArray(p.alerts.cloud);
const cloudText = (p: RoutingPolicy, lower = false) => {
  const picked = p.alerts.cloud || [];
  if (!picked.length) return lower ? 'any cloud server problem' : 'Any cloud server problem';
  const labels = picked.map((v) => CLOUD_PROBLEMS.find((c) => c.value === v)?.label || v);
  return listText(lower ? labels.map((l) => l.toLowerCase()) : labels, 4);
};
/** Site offline cards keep their sites in `cameras` (the directory lists sites there). */
const sitesText = (p: RoutingPolicy, names: Names, lower = false) => {
  if (p.cameras.mode === 'all') return lower ? 'any site' : 'All sites';
  return listText((p.cameras.ids || []).map((id) => names.camera.get(id) || 'a removed site'), 4);
};
/** Alerts from rules picked (absent = yes) / watchlist hits picked (an array, [] = every one). */
const pickedAi = (p: RoutingPolicy) => coversAi(p) && p.alerts.ai !== false;
const pickedWatch = (p: RoutingPolicy) => coversAi(p) && Array.isArray(p.alerts.watchlist);
const watchlistText = (p: RoutingPolicy, lower = false) => {
  const picked = p.alerts.watchlist || [];
  if (!picked.length) return lower ? 'any watchlist hit' : 'Any watchlist hit';
  const labels = picked.map((v) => WATCHLIST_ROUTING_CATEGORIES.find((c) => c.value === v)?.label || v);
  return lower ? `a watchlist hit (${listText(labels.map((l) => l.toLowerCase()), 5)})` : listText(labels, 5);
};
const systemKindsText = (p: RoutingPolicy, max = 5, lower = false) => {
  const kinds = p.alerts.kinds || [];
  if (!kinds.length) return lower ? 'any system problem' : 'Any system problem';
  return listText(kinds.map((k) => {
    const l = SYSTEM_ALERT_KINDS.find((x) => x.value === k)?.label || k;
    return lower ? l.toLowerCase() : l;
  }), max);
};

/** "When … happens on …" — what a notification catches. */
function whenText(p: RoutingPolicy, names: Names, eventTypeLabel: (t: string) => string): ReactNode {
  if (isOffline(p)) {
    if (p.is_default) return <>When <b>any other site</b> goes offline, or <b>any other cloud server problem</b> happens</>;
    const off = pickedOffline(p) ? <><b>{sitesText(p, names, true)}</b> goes offline</> : null;
    const cloud = pickedCloud(p) ? <><b>{cloudText(p, true)}</b> happens on the cloud server</> : null;
    return <>When {off}{off && cloud && ', or '}{cloud}</>;
  }
  if (p.is_default) return <>When <b>any other {defaultWhat(p)}</b> happens</>;
  const sys = <b>{systemKindsText(p, 5, true)}</b>;
  if (!coversAi(p)) return <>When {sys} happens</>;
  const types = p.alerts.event_types || [];
  const rules = p.alerts.rule_ids || [];
  const parts = [
    rules.length ? `an alert from ${listText(rules.map((id) => names.rule.get(id) || 'a removed rule'), 3)}` : '',
    types.length ? `a ${listText(types.map(eventTypeLabel), 2)} alert` : '',
  ].filter(Boolean);
  const aiWhat = pickedAi(p) ? (parts.join(' or ') || (coversSystem(p) || pickedWatch(p) ? 'any AI alert' : 'any alert')) : '';
  const what = [aiWhat, pickedWatch(p) ? watchlistText(p, true) : ''].filter(Boolean).join(' or ');
  const sev = p.alerts.min_severity ? ` (${SEVERITY_TEXT[p.alerts.min_severity]})` : '';
  const ids = p.cameras.ids || [];
  const map = p.cameras.mode === 'groups' ? names.group : names.camera;
  const where = p.cameras.mode === 'all' ? 'any camera' : `${listText(ids.map((id) => map.get(id) || 'a removed one'), 3)}${p.cameras.mode === 'groups' ? ' cameras' : ''}`;
  return <>When <b>{what}{sev}</b> happens on <b>{where}</b>{coversSystem(p) && <>, or {sys}</>}</>;
}

/** "Fire Detected (critical only)" — the AI alerts a notification catches, for its card. */
function aiAlertsText(p: RoutingPolicy, names: Names, eventTypeLabel: (t: string) => string): string {
  const rules = (p.alerts.rule_ids || []).map((id) => names.rule.get(id) || 'a removed rule');
  const types = (p.alerts.event_types || []).map(eventTypeLabel);
  const what = listText([...rules, ...types], 4) || 'Any AI alert';
  return p.alerts.min_severity ? `${what} — ${SEVERITY_TEXT[p.alerts.min_severity]}` : what;
}

/** What a notification catches, for its card: an "AI" line and/or a "System" line. */
function AlertsLines({ p, names, eventTypeLabel }: { p: RoutingPolicy; names: Names; eventTypeLabel: (t: string) => string }) {
  if (p.is_default) return <>Any {defaultWhat(p)} no other card catches</>;
  const tag = (t: string) => <span className="mr-1.5 inline-block w-16 shrink-0 text-[11px] font-semibold text-muted-foreground">{t}</span>;
  return (
    <div className="space-y-0.5">
      {pickedAi(p) && <div className="flex">{tag('AI')}<span className="min-w-0">{aiAlertsText(p, names, eventTypeLabel)}</span></div>}
      {pickedWatch(p) && <div className="flex">{tag('Watchlist')}<span className="min-w-0">{watchlistText(p)}{p.alerts.min_severity ? ` — ${SEVERITY_TEXT[p.alerts.min_severity]}` : ''}</span></div>}
      {coversSystem(p) && <div className="flex">{tag('System')}<span className="min-w-0">{systemKindsText(p)}</span></div>}
    </div>
  );
}

/** A Cloud (platform) card's "Offline" and/or "Cloud" line. */
function CloudLines({ p, names }: { p: RoutingPolicy; names: Names }) {
  const tag = (t: string) => <span className="mr-1.5 inline-block w-16 shrink-0 text-[11px] font-semibold text-muted-foreground">{t}</span>;
  return (
    <div className="space-y-0.5">
      {pickedOffline(p) && <div className="flex">{tag('Offline')}<span className="min-w-0">{sitesText(p, names)}</span></div>}
      {pickedCloud(p) && <div className="flex">{tag('Cloud')}<span className="min-w-0">{cloudText(p)}</span></div>}
    </div>
  );
}

function camerasText(p: RoutingPolicy, names: Names): string {
  if (p.cameras.mode === 'all') return 'All cameras';
  const map = p.cameras.mode === 'groups' ? names.group : names.camera;
  const list = listText((p.cameras.ids || []).map((id) => map.get(id) || 'a removed one'), 4);
  return p.cameras.mode === 'groups' ? `Cameras in ${list}` : list;
}

/** How one person is told, as small badges. */
function WayBadges({ ch }: { ch?: PolicyChannels }) {
  return (
    <span className="inline-flex flex-wrap gap-1">
      {CHANNEL_KEYS.filter((k) => ch?.[k]).map((k) => {
        const Icon = CHANNEL_ICON[k];
        return (
          <span key={k} className="inline-flex items-center gap-0.5 rounded border border-border bg-card px-1.5 text-[11px] text-foreground">
            <Icon className="h-3 w-3 text-muted-foreground" /> {CHANNEL_CHIP[k]}
          </span>
        );
      })}
    </span>
  );
}

/** Each picked person / role / group on its own line, with how they are told. */
function PeopleList({ people, names }: { people: PolicyPerson[]; names: Names }) {
  if (!people.length) return <span className="text-destructive">Nobody yet</span>;
  return (
    <ul className="space-y-1">
      {people.map((p, i) => {
        const g = p.kind === 'group' ? names.contactGroup.get(p.id || '') : null;
        const Icon = p.kind === 'group' ? UsersIcon : p.kind === 'role' ? UserGroupIcon : UserIcon;
        return (
          <li key={i} className="flex flex-wrap items-center gap-x-2 gap-y-0.5">
            <Icon className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
            <span className="text-foreground">{p.kind === 'role' ? roleLabel(p.id, names) : personLabel(p, names)}</span>
            {p.kind === 'group'
              ? <span className="text-[11px] text-muted-foreground">contact group · {g ? `${g.members.length} ${g.members.length === 1 ? 'person' : 'people'}, each told their own way` : 'removed'}</span>
              : <WayBadges ch={p.channels} />}
          </li>
        );
      })}
    </ul>
  );
}

/** "tell … by …" and the "if nobody acknowledges" steps. */
function tellText(p: RoutingPolicy, names: Names): { first: ReactNode; later: string | null } {
  const who = peopleText(p.levels?.[0]?.people || [], names);
  const first = who ? <>tell <b>{who}</b>.</> : <>tell <b className="text-destructive">nobody yet</b>.</>;
  const steps = (p.levels || []).slice(1).map((l) => `after ${l.after_min} min tell ${peopleText(l.people, names) || 'nobody'}`);
  if (p.reminder && (p.levels || []).length) steps.push(`then remind them every ${p.reminder.every_min} min (up to ${p.reminder.max}×)`);
  return { first, later: steps.length ? `If nobody acknowledges: ${steps.join(' · ')}.` : null };
}

// ─── Pickers ────────────────────────────────────────────────────────

export function Choice({ on, onClick, children }: { on: boolean; onClick: () => void; children: ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={on}
      className={`rounded-lg border px-3 py-1.5 text-[13px] transition-colors ${on ? 'border-brand bg-brand-light font-medium text-brand' : 'border-border text-foreground hover:bg-muted'}`}
    >
      {children}
    </button>
  );
}

export function Step({ n, title, hint, children }: { n: number; title: string; hint?: string; children: ReactNode }) {
  return (
    <section className="flex gap-3">
      <span className="mt-0.5 flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-brand text-[12px] font-semibold text-white">{n}</span>
      <div className="min-w-0 flex-1 space-y-2">
        <div>
          <h4 className="text-[14px] font-semibold text-foreground">{title}</h4>
          {hint && <p className="text-[12px] text-muted-foreground">{hint}</p>}
        </div>
        {children}
      </div>
    </section>
  );
}

/** A searchable list of checkboxes — camera groups, cameras, rules. */
export function MultiPick({ options, selected, onChange, placeholder, empty }: {
  options: { id: string; label: string; hint?: string }[];
  selected: string[];
  onChange: (next: string[]) => void;
  placeholder: string;
  empty: string;
}) {
  const [q, setQ] = useState('');
  const shown = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return needle ? options.filter((o) => `${o.label} ${o.hint || ''}`.toLowerCase().includes(needle)) : options;
  }, [options, q]);
  const toggle = (id: string) => onChange(selected.includes(id) ? selected.filter((x) => x !== id) : [...selected, id]);
  return (
    <div className="max-w-xl rounded-lg border border-border">
      <div className="flex items-center gap-1.5 border-b border-border px-2 py-1">
        <MagnifyingGlassIcon className="h-3.5 w-3.5 text-muted-foreground" />
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={placeholder} className="w-full bg-transparent py-0.5 text-[13px] text-foreground outline-none placeholder:text-muted-foreground" />
        {selected.length > 0 && <span className="shrink-0 text-[11px] text-muted-foreground">{selected.length} picked</span>}
      </div>
      <div className="max-h-40 overflow-y-auto py-1">
        {shown.length === 0 && <p className="px-2.5 py-1.5 text-[12px] text-muted-foreground">{empty}</p>}
        {shown.map((o) => (
          <label key={o.id} className="flex cursor-pointer items-start gap-2 px-2.5 py-1 text-[13px] text-foreground hover:bg-muted">
            <input type="checkbox" checked={selected.includes(o.id)} onChange={() => toggle(o.id)} className="mt-0.5 accent-brand" />
            <span>{o.label}{o.hint && <span className="ml-1.5 text-[11px] text-muted-foreground">{o.hint}</span>}</span>
          </label>
        ))}
      </div>
    </div>
  );
}


/** The three ways to tell one person, as toggles. */
function ChannelChips({ value, onChange, noDashboard, noEmail, noPhone }: {
  value: PolicyChannels; onChange: (v: PolicyChannels) => void; noDashboard?: boolean; noEmail?: boolean; noPhone?: boolean;
}) {
  return (
    <span className="inline-flex flex-wrap gap-1">
      {CHANNEL_KEYS.map((k) => {
        const off = (k === 'dashboard' && noDashboard) || (k === 'email' && noEmail) || (k === 'message_api' && noPhone);
        const Icon = CHANNEL_ICON[k];
        const on = !!value[k] && !off;
        return (
          <button
            key={k}
            type="button"
            disabled={off}
            aria-pressed={on}
            title={off ? (k === 'dashboard' ? 'Only people with an account get pop-ups' : k === 'email' ? 'No email address' : 'No phone number') : CHANNEL_HINT[k]}
            onClick={() => onChange({ ...value, [k]: !value[k] })}
            className={`inline-flex items-center gap-1 rounded-md border px-1.5 py-0.5 text-[11px] ${on ? 'border-brand bg-brand-light font-medium text-brand' : 'border-border text-muted-foreground hover:bg-muted'} disabled:cursor-not-allowed disabled:opacity-40`}
          >
            <Icon className="h-3.5 w-3.5" /> {CHANNEL_CHIP[k]}
          </button>
        );
      })}
    </span>
  );
}

/**
 * Who to tell, and how: one search box for people, roles and contact groups; each pick is a row
 * with its own Pop-up / Email / WhatsApp toggles (a group's members keep the ways set in the
 * group); people without an account are added with a name and an email or phone number.
 */
function PeoplePicker({ people, onChange, dir, names, allowGroups = true, onNewGroup }: {
  people: PolicyPerson[]; onChange: (next: PolicyPerson[]) => void; dir: PolicyDirectory | null; names: Names; allowGroups?: boolean;
  /** "+ New contact group" — the host opens the group editor and adds the new group here. */
  onNewGroup?: () => void;
}) {
  const [q, setQ] = useState('');
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    const close = (e: MouseEvent) => { if (box.current && !box.current.contains(e.target as Node)) setOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);
  const has = (kind: string, id: string) => people.some((p) => p.kind === kind && p.id === id);
  const needle = q.trim().toLowerCase();
  const users = (dir?.users || []).filter((u) => !has('user', u.id) && (!needle || `${u.name} ${u.email} ${u.role}`.toLowerCase().includes(needle))).slice(0, 8);
  const roles = (dir?.roles || []).filter((x) => !has('role', x.id) && (!needle || x.name.toLowerCase().includes(needle))).slice(0, 5);
  const groups = allowGroups ? (dir?.contact_groups || []).filter((g) => !has('group', g.id) && (!needle || g.name.toLowerCase().includes(needle))).slice(0, 6) : [];
  const userOf = (id?: string) => dir?.users.find((u) => u.id === id);
  const add = (p: PolicyPerson) => { onChange([...people, p]); setQ(''); };
  const setAt = (i: number, patch: Partial<PolicyPerson>) => onChange(people.map((p, j) => (j === i ? { ...p, ...patch } : p)));
  const removeAt = (i: number) => onChange(people.filter((_, j) => j !== i));
  const rowCls = 'flex flex-wrap items-center gap-x-2 gap-y-1 rounded-lg border border-border bg-card px-2.5 py-1.5';

  return (
    <div className="max-w-2xl space-y-2">
      {people.length > 0 && (
        <ul className="space-y-1.5">
          {people.map((p, i) => {
            const remove = (
              <button type="button" onClick={() => removeAt(i)} className="ml-auto rounded-md p-0.5 text-muted-foreground hover:bg-muted hover:text-destructive" aria-label="Remove">
                <XMarkIcon className="h-4 w-4" />
              </button>
            );
            if (p.kind === 'contact') {
              return (
                <li key={i} className={rowCls}>
                  <UserIcon className="h-4 w-4 shrink-0 text-muted-foreground" />
                  <input value={p.name || ''} onChange={(e) => setAt(i, { name: e.target.value })} placeholder="Name" className="w-28 min-w-0 rounded-md border border-border bg-background px-1.5 py-0.5 text-[12px]" />
                  <input value={p.email || ''} onChange={(e) => setAt(i, { email: e.target.value })} placeholder="Email" className="w-40 min-w-0 rounded-md border border-border bg-background px-1.5 py-0.5 text-[12px]" />
                  <input value={p.phone || ''} onChange={(e) => setAt(i, { phone: e.target.value })} placeholder="Phone, e.g. 919876543210" className="w-40 min-w-0 rounded-md border border-border bg-background px-1.5 py-0.5 text-[12px]" />
                  <ChannelChips value={p.channels || { dashboard: false, email: true, message_api: true }} onChange={(channels) => setAt(i, { channels })} noDashboard noEmail={!p.email} noPhone={!p.phone} />
                  {remove}
                </li>
              );
            }
            if (p.kind === 'group') {
              const g = names.contactGroup.get(p.id || '');
              return (
                <li key={i} className={rowCls}>
                  <UsersIcon className="h-4 w-4 shrink-0 text-muted-foreground" />
                  <span className="text-[13px] font-medium text-foreground">{g?.name || 'A removed group'}</span>
                  <span className="text-[12px] text-muted-foreground">
                    {g ? `${g.members.length} ${g.members.length === 1 ? 'person' : 'people'} — each told as set in the group` : ''}
                  </span>
                  {remove}
                </li>
              );
            }
            const u = p.kind === 'user' ? userOf(p.id) : null;
            return (
              <li key={i} className={rowCls}>
                {p.kind === 'role' ? <UserGroupIcon className="h-4 w-4 shrink-0 text-muted-foreground" /> : <UserIcon className="h-4 w-4 shrink-0 text-muted-foreground" />}
                <span className="min-w-0 text-[13px] text-foreground">
                  {p.kind === 'role' ? <b className="font-medium">{roleLabel(p.id, names)}</b> : u?.name || 'A removed user'}
                </span>
                {u && !u.phone && <span className="text-[11px] text-muted-foreground">no phone</span>}
                <span className="flex-1" />
                <ChannelChips value={p.channels || { dashboard: true, email: true, message_api: false }} onChange={(channels) => setAt(i, { channels })} noPhone={!!u && !u.phone} noEmail={!!u && !u.email} />
                {remove}
              </li>
            );
          })}
        </ul>
      )}

      <div ref={box} className="relative max-w-xl">
        <div className="flex items-center gap-1.5 rounded-lg border border-border bg-background px-2.5 py-1.5">
          <MagnifyingGlassIcon className="h-4 w-4 text-muted-foreground" />
          <input
            value={q}
            onChange={(e) => { setQ(e.target.value); setOpen(true); }}
            onFocus={() => setOpen(true)}
            placeholder={allowGroups ? 'Type a name, a role or a contact group…' : 'Type a name or a role…'}
            className="w-full bg-transparent text-[13px] text-foreground outline-none placeholder:text-muted-foreground"
          />
        </div>
        {open && (users.length > 0 || roles.length > 0 || groups.length > 0) && (
          <div className="absolute z-20 mt-1 max-h-72 w-full overflow-y-auto rounded-lg border border-border bg-card py-1 shadow-lg">
            {groups.length > 0 && <p className="px-3 pb-0.5 pt-1 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Contact groups</p>}
            {groups.map((g) => (
              <button key={g.id} type="button" onClick={() => add({ kind: 'group', id: g.id })}
                className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-[13px] text-foreground hover:bg-muted">
                <UsersIcon className="h-4 w-4 text-muted-foreground" />
                <span className="min-w-0 flex-1 truncate">{g.name}</span>
                <span className="shrink-0 text-[11px] text-muted-foreground">{g.members.length} {g.members.length === 1 ? 'person' : 'people'}</span>
              </button>
            ))}
            {users.length > 0 && <p className="px-3 pb-0.5 pt-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">People</p>}
            {users.map((u) => (
              <button key={u.id} type="button" onClick={() => add({ kind: 'user', id: u.id, channels: { dashboard: true, email: !!u.email, message_api: !!u.phone } })}
                className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-[13px] text-foreground hover:bg-muted">
                <UserIcon className="h-4 w-4 text-muted-foreground" />
                <span className="min-w-0 flex-1 truncate">{u.name}</span>
                <span className="shrink-0 text-[11px] text-muted-foreground">{[u.role, u.phone ? null : 'no phone'].filter(Boolean).join(' · ')}</span>
              </button>
            ))}
            {roles.length > 0 && <p className="px-3 pb-0.5 pt-1.5 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Everyone with a role</p>}
            {roles.map((x) => (
              <button key={x.id} type="button" onClick={() => add({ kind: 'role', id: x.id, channels: { dashboard: true, email: true, message_api: false } })}
                className="flex w-full items-center gap-2 px-3 py-1.5 text-left text-[13px] text-foreground hover:bg-muted">
                <UserGroupIcon className="h-4 w-4 text-muted-foreground" /> {roleLabel(x.id, names)}
              </button>
            ))}
          </div>
        )}
      </div>

      {allowGroups && (dir?.contact_groups?.length || onNewGroup) && (
        <div className="flex max-w-xl flex-wrap items-center gap-1.5 rounded-lg border border-dashed border-border px-2.5 py-1.5 text-[12px] text-foreground">
          <UsersIcon className="h-4 w-4 text-muted-foreground" />
          <span>Add a contact group:</span>
          {(dir?.contact_groups || []).some((g) => !has('group', g.id)) && (
            <select
              value=""
              onChange={(e) => { if (e.target.value) add({ kind: 'group', id: e.target.value }); }}
              className="rounded-md border border-border bg-background px-1.5 py-0.5 text-[12px] text-foreground"
              aria-label="Pick a contact group"
            >
              <option value="">pick an existing group…</option>
              {(dir?.contact_groups || []).filter((g) => !has('group', g.id)).map((g) => (
                <option key={g.id} value={g.id}>{g.name} ({g.members.length})</option>
              ))}
            </select>
          )}
          {onNewGroup && (
            <>
              {(dir?.contact_groups || []).some((g) => !has('group', g.id)) && <span className="text-muted-foreground">or</span>}
              <button type="button" onClick={onNewGroup} className="inline-flex items-center gap-1 font-medium text-brand hover:underline">
                <PlusIcon className="h-3.5 w-3.5" /> New contact group
              </button>
            </>
          )}
        </div>
      )}

      <button
        type="button"
        onClick={() => onChange([...people, { kind: 'contact', name: '', email: '', phone: '', channels: { dashboard: false, email: true, message_api: true } }])}
        className="inline-flex items-center gap-1 text-[12px] font-medium text-brand hover:underline"
      >
        <UserPlusIcon className="h-4 w-4" /> Add someone without an account
      </button>
    </div>
  );
}

/** Only the people the backend can use: a contact needs an address for each way picked. */
function cleanPeople(people: PolicyPerson[]): PolicyPerson[] {
  return people
    .filter((p) => p.kind !== 'contact' || p.email || p.phone)
    .map((p) => (p.kind === 'contact'
      ? { ...p, channels: { dashboard: false, email: !!p.channels?.email && !!p.email, message_api: !!p.channels?.message_api && !!p.phone } }
      : p.kind === 'group' ? { kind: 'group' as const, id: p.id } : p));
}

const noWay = (people: PolicyPerson[]) => people.some((p) => p.kind !== 'group' && !CHANNEL_KEYS.some((k) => p.channels?.[k]));

// ─── Editor ─────────────────────────────────────────────────────────

const nobodyIn = (l: PolicyLevel) => !cleanPeople(l.people || []).length;

function autoName(p: RoutingPolicy, names: Names, eventTypeLabel: (t: string) => string): string {
  if (isOffline(p)) return [pickedOffline(p) ? `${sitesText(p, names)} offline` : '', pickedCloud(p) ? (p.alerts.cloud?.length ? `Cloud: ${cloudText(p)}` : 'Cloud server') : ''].filter(Boolean).join(' + ');
  const sys = (p.alerts.kinds || []).length ? systemKindsText(p, 2) : 'All system problems';
  if (!coversAi(p)) return sys;
  const rules = (p.alerts.rule_ids || []).map((id) => names.rule.get(id) || '?');
  const types = (p.alerts.event_types || []).map(eventTypeLabel);
  const aiWhat = !pickedAi(p) ? '' : rules.length ? listText(rules, 2) : types.length ? listText(types, 2) : 'All alerts';
  const what = [aiWhat, pickedWatch(p) ? (p.alerts.watchlist?.length ? watchlistText(p) : 'Watchlist') : ''].filter(Boolean).join(' + ');
  const ids = p.cameras.ids || [];
  const map = p.cameras.mode === 'groups' ? names.group : names.camera;
  const ai = p.cameras.mode === 'all' ? what : `${what} — ${listText(ids.map((id) => map.get(id) || '?'), 2)}`;
  return coversSystem(p) ? `${ai} + ${sys}` : ai;
}

/**
 * The escalation as a tree, drawn from the notification as it stands: what catches it → who is
 * told at once → each "if nobody acknowledges" step → reminders → never answered, with the
 * "acknowledged → stops" branch off every step.
 */
function EscalationTree({ p, names, eventTypeLabel }: { p: RoutingPolicy; names: Names; eventTypeLabel: (t: string) => string }) {
  const levels = p.levels || [];
  const stop = (
    <span className="inline-flex shrink-0 items-center gap-1 self-center rounded-full border border-success px-2 py-0.5 text-[11px] font-medium text-success">
      ✓ acknowledged → stops here
    </span>
  );
  const Node = ({ dot, title, children, branch }: { dot: string; title: ReactNode; children?: ReactNode; branch?: boolean }) => (
    <li className="relative">
      <span className={`absolute -left-[29px] top-3 h-3.5 w-3.5 rounded-full border-2 border-card ${dot}`} />
      <div className="flex flex-wrap items-stretch gap-2">
        <div className="min-w-0 max-w-xl flex-1 rounded-lg border border-border bg-card px-3 py-2">
          <p className="text-[12px] font-semibold text-foreground">{title}</p>
          {children && <div className="text-[12px] text-muted-foreground">{children}</div>}
        </div>
        {branch && (
          <div className="flex items-center gap-1">
            <span className="h-px w-4 bg-success" />
            {stop}
          </div>
        )}
      </div>
    </li>
  );
  const who = (l: PolicyLevel) => {
    const w = peopleText(l.people || [], names);
    return w ? <b className="text-foreground">{w}</b> : <span className="text-destructive">nobody picked</span>;
  };
  return (
    <ol className="ml-4 space-y-3 border-l-2 border-border pl-6">
      <Node dot="bg-brand" title={isOffline(p) ? (p.is_default || (pickedOffline(p) && pickedCloud(p)) ? 'A site goes offline, or a cloud server problem starts' : pickedCloud(p) ? 'A cloud server problem starts' : 'A site goes offline') : p.kind === 'ai' ? 'An alert comes in' : p.kind === 'system' ? 'A system problem starts' : 'An alert or a system problem comes in'}>
        {whenText(p, names, eventTypeLabel)}
      </Node>
      {levels.map((l, i) => (
        <Node
          key={i}
          dot={i === 0 ? 'bg-info' : 'bg-warning'}
          title={i === 0 ? 'Told at once' : `Still not acknowledged after ${l.after_min} min — tell`}
          branch
        >
          {who(l)}
        </Node>
      ))}
      {p.reminder && levels.length > 0 && (
        <Node dot="bg-warning" title={`Reminded every ${p.reminder.every_min} min, up to ${p.reminder.max} times`} branch>
          {who(levels[levels.length - 1])}
        </Node>
      )}
      <Node dot="bg-destructive" title="Nobody acknowledged">
        Counted as <b className="text-destructive">never answered</b> in the Escalations report.
      </Node>
    </ol>
  );
}

/** A tick-box with its choices under it (shown only when ticked). `only` = the last one ticked. */
function ScopeBox({ on, only, onChange, title, note, children }: {
  on: boolean; only: boolean; onChange: (on: boolean) => void; title: string; note: string; children: ReactNode;
}) {
  return (
    <div className={`max-w-2xl rounded-xl border px-3 py-2.5 ${on ? 'border-brand bg-card' : 'border-border bg-muted'}`}>
      <label className={`flex items-center gap-2.5 ${only ? 'cursor-default' : 'cursor-pointer'}`} title={only ? 'Tick the other one first — a notification needs at least one' : undefined}>
        <input type="checkbox" checked={on} disabled={only} onChange={(e) => onChange(e.target.checked)} className="h-4 w-4 accent-brand" />
        <span className="text-[13px] font-semibold text-foreground">{title}</span>
        <span className="text-[12px] text-muted-foreground">{note}</span>
      </label>
      {on && <div className="mt-2.5 space-y-2.5 pl-6">{children}</div>}
    </div>
  );
}

function PolicyEditor({ open, initial, dir, names, api, eventTypeLabel, onClose, onSaved, onGroupsChanged, variant = 'alerts' }: {
  variant?: 'alerts' | 'offline';
  open: boolean;
  initial: RoutingPolicy | null;
  dir: PolicyDirectory | null;
  names: Names;
  api: RoutingPoliciesApi;
  eventTypeLabel: (t: string) => string;
  onClose: () => void;
  onSaved: (p: RoutingPolicy) => void;
  /** A contact group was made here: read the groups again. */
  onGroupsChanged: () => void;
}) {
  // Which step's "New contact group" is open (its level index), null = none.
  const [newGroupFor, setNewGroupFor] = useState<number | null>(null);
  const blank = (): RoutingPolicy => ({
    id: '', name: '', kind: variant === 'offline' ? 'offline' : 'ai', is_default: false, enabled: true, position: 0,
    cameras: { mode: 'all', ids: [] },
    alerts: variant === 'offline' ? { offline: true, cloud: null } : { ai: true, event_types: [], rule_ids: [], min_severity: null, watchlist: null, kinds: [] },
    levels: [emptyPolicyLevel()],
    reminder: null,
    board: true,
  });
  const [p, setP] = useState<RoutingPolicy>(initial || blank());
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  // "Alerts from these rules" (picked) or "All alerts".
  const [byRule, setByRule] = useState(true);
  useEffect(() => {
    if (!open) return;
    setP(initial ? { ...initial, levels: initial.levels?.length ? initial.levels : [emptyPolicyLevel()] } : blank());
    setByRule(initial ? initial.kind === 'system' || !!initial.alerts?.rule_ids?.length || !!initial.alerts?.event_types?.length : true);
    setError('');
  }, [open, initial]); // eslint-disable-line react-hooks/exhaustive-deps

  const setPeople = (i: number, people: PolicyPerson[]) => setP((x) => ({ ...x, levels: x.levels.map((l, j) => (j === i ? { ...l, people } : l)) }));
  const setAfter = (i: number, after_min: number) => setP((x) => ({ ...x, levels: x.levels.map((l, j) => (j === i ? { ...l, after_min } : l)) }));
  const isDefault = p.is_default;
  const wantAi = pickedAi(p);
  const wantWl = pickedWatch(p);
  const wantSys = coversSystem(p);
  const offline = isOffline(p);
  const wantOff = pickedOffline(p);
  const wantCloud = pickedCloud(p);
  const cloudPicked = p.alerts.cloud || [];
  // Site offline / cloud server — at least one stays ticked.
  const setCloudScope = (off: boolean, cloud: boolean) => {
    if (!off && !cloud) return;
    setP((x) => ({ ...x, alerts: { ...x.alerts, offline: off, cloud: cloud ? (Array.isArray(x.alerts.cloud) ? x.alerts.cloud : []) : null } }));
  };
  // Camera alerts of either kind: cameras, severity and the alert board apply to them.
  const camPath = wantAi || wantWl;
  const ticked = [wantAi, wantWl, wantSys].filter(Boolean).length;
  // Tick AI alerts / watchlist / system problems — at least one stays ticked.
  const setScope = (ai: boolean, wl: boolean, sys: boolean) => {
    if (!ai && !wl && !sys) return;
    setP((x) => ({
      ...x,
      kind: (ai || wl) && sys ? 'both' : ai || wl ? 'ai' : 'system',
      alerts: { ...x.alerts, ai, watchlist: wl ? (Array.isArray(x.alerts.watchlist) ? x.alerts.watchlist : []) : null },
    }));
  };
  const watch = p.alerts.watchlist || [];
  const escalates = p.levels.length > 1 || !!p.reminder;
  const types = p.alerts.event_types || [];
  const toggleType = (t: string) => setP({ ...p, alerts: { ...p.alerts, event_types: types.includes(t) ? types.filter((x) => x !== t) : [...types, t] } });
  const kinds = p.alerts.kinds || [];
  const setCameraMode = (mode: RoutingPolicy['cameras']['mode']) => setP({ ...p, cameras: { mode, ids: mode === p.cameras.mode ? p.cameras.ids : [] } });
  // Older rules still limited to some cameras — say so next to the rule.
  const ruleHint = (r: PolicyDirectory['rules'][number]) => [
    eventTypeLabel(r.event_type),
    r.is_active ? null : '(off)',
    r.cameras?.length || r.group_id ? '(only some cameras — an older rule)' : null,
  ].filter(Boolean).join(' · ');

  const save = async () => {
    const tooEarly = p.levels.findIndex((l, i) => i > 0 && !(l.after_min > p.levels[i - 1].after_min));
    if (tooEarly > 0) return setError(`Step ${tooEarly + 1} has to come later than the one before it.`);
    if (!isDefault && wantAi && byRule && !(p.alerts.rule_ids || []).length && !(p.alerts.event_types || []).length) {
      return setError('Pick at least one alert rule — or choose "All AI alerts".');
    }
    if (!isDefault && wantOff && p.cameras.mode !== 'all' && !p.cameras.ids.length) return setError('Pick at least one site — or choose "All sites".');
    if (!isDefault && camPath && p.cameras.mode !== 'all' && !p.cameras.ids.length) {
      return setError(p.cameras.mode === 'groups' ? 'Pick at least one camera group — or choose "All cameras".' : 'Pick at least one camera — or choose "All cameras".');
    }
    if (p.levels.some((l) => noWay(l.people || []))) return setError('Someone has no way picked — tick Pop-up, Email or WhatsApp for each person.');
    // Someone without an account needs somewhere to be reached — never dropped without a word.
    const noAddress = p.levels.flatMap((l) => l.people || []).find((x) => x.kind === 'contact' && !x.email && !x.phone && String(x.name || '').trim());
    if (noAddress) return setError(`Add an email or a phone number for ${(noAddress as any).name} — or remove them.`);
    if (p.reminder && !(p.reminder.every_min >= 1 && p.reminder.every_min <= 1440 && p.reminder.max >= 1 && p.reminder.max <= 10)) {
      return setError('Reminders: every 1 – 1440 minutes, 1 – 10 times.');
    }
    setSaving(true);
    setError('');
    try {
      const body: Partial<RoutingPolicy> = {
        enabled: p.enabled,
        reminder: p.reminder || null,
        levels: p.levels.map((l) => ({ after_min: l.after_min, people: cleanPeople(l.people || []) })),
        ...(wantAi ? { board: p.board !== false } : {}),
      };
      if (!isDefault && offline) {
        Object.assign(body, {
          kind: 'offline', name: p.name.trim() || autoName(p, names, eventTypeLabel),
          cameras: wantOff ? p.cameras : { mode: 'all', ids: [] },
          alerts: { offline: wantOff, cloud: wantCloud ? cloudPicked : null },
        });
      } else if (!isDefault) {
        const ai = {
          ai: wantAi,
          ...(wantAi && byRule
            ? { rule_ids: p.alerts.rule_ids || [], event_types: p.alerts.event_types || [] }
            : { rule_ids: [], event_types: [] }),
          min_severity: p.alerts.min_severity || null,
          watchlist: wantWl ? watch : null,
        };
        const sys = { kinds: p.alerts.kinds || [] };
        Object.assign(body, {
          kind: p.kind,
          name: p.name.trim() || autoName(p, names, eventTypeLabel),
          cameras: camPath ? p.cameras : { mode: 'all', ids: [] },
          alerts: p.kind === 'both' ? { ...ai, ...sys } : p.kind === 'ai' ? ai : sys,
        });
      }
      onSaved(await api.save(initial?.id || null, body));
    } catch (err) {
      setError(alertActionsErrorText(err, 'Could not save'));
    } finally {
      setSaving(false);
    }
  };

  const summary = tellText(p, names);
  let n = 0;
  const next = () => { n += 1; return n; };

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="xl"
      title={isDefault ? `Everything else${defaultSuffix(p) ? ` — ${defaultSuffix(p)}` : ''}` : initial ? 'Edit notification' : 'New notification'}
      subtitle={isDefault ? `Any ${defaultWhat(p)} that no other notification catches.` : undefined}
      footer={(
        <div className="flex flex-wrap items-center gap-2">
          <span className="min-w-0 flex-1 text-[12px] text-destructive">{error}</span>
          <button type="button" onClick={onClose} className="rounded-lg border border-border px-3 py-1.5 text-[13px] text-foreground hover:bg-muted">Cancel</button>
          <button type="button" onClick={save} disabled={saving} className="rounded-lg bg-brand px-4 py-1.5 text-[13px] font-medium text-white hover:opacity-90 disabled:opacity-50">
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      )}
    >
      <div className="space-y-5 p-5">
        <p className="rounded-xl border border-border bg-muted px-3 py-2.5 text-[13px] leading-relaxed text-foreground">
          {whenText(p, names, eventTypeLabel)}, {summary.first}
          {summary.later && <span className="mt-1 block">{summary.later}</span>}
        </p>

        {!isDefault && (
          <Step n={next()} title="Name">
            <input value={p.name} onChange={(e) => setP({ ...p, name: e.target.value })} placeholder={autoName(p, names, eventTypeLabel)} className={`${aaInputCls} max-w-md`} />
          </Step>
        )}

        {!isDefault && offline && (
          <Step n={next()} title="Which alerts?">
            <ScopeBox on={wantOff} only={wantOff && !wantCloud} onChange={(on) => setCloudScope(on, wantCloud)} title="Site offline" note="A site stops reporting">
              <div className="flex flex-wrap gap-1.5">
                <Choice on={p.cameras.mode === 'all'} onClick={() => setCameraMode('all')}>All sites</Choice>
                <Choice on={p.cameras.mode === 'cameras'} onClick={() => setCameraMode('cameras')}>Some sites</Choice>
              </div>
              {p.cameras.mode === 'cameras' && (
                <MultiPick
                  options={(dir?.cameras || []).map((c) => ({ id: c.id, label: c.name }))}
                  selected={p.cameras.ids}
                  onChange={(ids) => setP({ ...p, cameras: { mode: 'cameras', ids } })}
                  placeholder="Find a site"
                  empty="No sites registered yet."
                />
              )}
            </ScopeBox>
            <ScopeBox on={wantCloud} only={wantCloud && !wantOff} onChange={(on) => setCloudScope(wantOff, on)} title="Cloud server" note="The cloud's own CPU, memory, disk and critical errors">
              <div className="flex flex-wrap gap-1.5">
                <Choice on={cloudPicked.length === 0} onClick={() => setP({ ...p, alerts: { ...p.alerts, cloud: [] } })}>Any problem</Choice>
                {CLOUD_PROBLEMS.map((c) => (
                  <Choice key={c.value} on={cloudPicked.includes(c.value)} onClick={() => setP({ ...p, alerts: { ...p.alerts, cloud: cloudPicked.includes(c.value) ? cloudPicked.filter((x) => x !== c.value) : [...cloudPicked, c.value] } })}>
                    {c.label}
                  </Choice>
                ))}
              </div>
            </ScopeBox>
          </Step>
        )}

        {!isDefault && !offline && (
          <Step n={next()} title="Which alerts?">
            <ScopeBox on={wantAi} only={wantAi && ticked === 1} onChange={(on) => setScope(on, wantWl, wantSys)} title="AI alerts" note="From the alert rules">
              <div className="flex flex-wrap gap-1.5">
                <Choice on={byRule} onClick={() => setByRule(true)}>From these rules</Choice>
                <Choice on={!byRule} onClick={() => setByRule(false)}>All AI alerts</Choice>
              </div>
              {byRule && (
                <MultiPick
                  options={(dir?.rules || []).map((r) => ({ id: r.id, label: r.name || eventTypeLabel(r.event_type), hint: ruleHint(r) }))}
                  selected={p.alerts.rule_ids || []}
                  onChange={(rule_ids) => setP({ ...p, alerts: { ...p.alerts, rule_ids } })}
                  placeholder="Find an alert rule"
                  empty="This site has no alert rules yet — create one on the Alert Config tab."
                />
              )}
              {byRule && types.length > 0 && (
                <div className="flex max-w-xl flex-wrap items-center gap-1.5 text-[12px] text-muted-foreground">
                  Also catches these alert types (an older setting):
                  {types.map((t) => (
                    <span key={t} className="inline-flex items-center gap-1 rounded-full border border-border bg-muted py-0.5 pl-2 pr-1 text-foreground">
                      {eventTypeLabel(t)}
                      <button type="button" onClick={() => toggleType(t)} className="rounded-full p-0.5 hover:bg-background" aria-label="Remove"><XMarkIcon className="h-3 w-3" /></button>
                    </span>
                  ))}
                </div>
              )}
            </ScopeBox>
            <ScopeBox on={wantWl} only={wantWl && ticked === 1} onChange={(on) => setScope(wantAi, on, wantSys)} title="Watchlist" note="Listed people and vehicles seen — always on the alert board">
              <div className="flex flex-wrap gap-1.5">
                <Choice on={watch.length === 0} onClick={() => setP({ ...p, alerts: { ...p.alerts, watchlist: [] } })}>Any watchlist hit</Choice>
                {WATCHLIST_ROUTING_CATEGORIES.map((c) => (
                  <Choice key={c.value} on={watch.includes(c.value)} onClick={() => setP({ ...p, alerts: { ...p.alerts, watchlist: watch.includes(c.value) ? watch.filter((x) => x !== c.value) : [...watch, c.value] } })}>
                    {c.label}
                  </Choice>
                ))}
              </div>
            </ScopeBox>
            <ScopeBox on={wantSys} only={wantSys && ticked === 1} onChange={(on) => setScope(wantAi, wantWl, on)} title="System problems" note="Cameras offline, services down, CPU, memory, disk">
              <div className="flex flex-wrap gap-1.5">
                <Choice on={kinds.length === 0} onClick={() => setP({ ...p, alerts: { ...p.alerts, kinds: [] } })}>Any problem</Choice>
                {SYSTEM_ALERT_KINDS.map((k) => (
                  <Choice key={k.value} on={kinds.includes(k.value)} onClick={() => setP({ ...p, alerts: { ...p.alerts, kinds: kinds.includes(k.value) ? kinds.filter((x) => x !== k.value) : [...kinds, k.value] } })}>
                    {k.label}
                  </Choice>
                ))}
              </div>
            </ScopeBox>
            {camPath && (
              <div className="flex flex-wrap items-center gap-1.5">
                <span className="text-[12px] text-muted-foreground">How serious?</span>
                <Choice on={!p.alerts.min_severity} onClick={() => setP({ ...p, alerts: { ...p.alerts, min_severity: null } })}>All</Choice>
                <Choice on={p.alerts.min_severity === 'warning'} onClick={() => setP({ ...p, alerts: { ...p.alerts, min_severity: 'warning' } })}>Warnings and critical</Choice>
                <Choice on={p.alerts.min_severity === 'critical'} onClick={() => setP({ ...p, alerts: { ...p.alerts, min_severity: 'critical' } })}>Critical only</Choice>
              </div>
            )}
          </Step>
        )}

        {!isDefault && camPath && (
          <Step n={next()} title="Which cameras?" hint={wantSys ? 'System problems come from the whole site.' : undefined}>
            <div className="flex flex-wrap gap-1.5">
              <Choice on={p.cameras.mode === 'all'} onClick={() => setCameraMode('all')}>All cameras</Choice>
              <Choice on={p.cameras.mode === 'groups'} onClick={() => setCameraMode('groups')}>Camera groups</Choice>
              <Choice on={p.cameras.mode === 'cameras'} onClick={() => setCameraMode('cameras')}>Some cameras</Choice>
            </div>
            {p.cameras.mode === 'groups' && (
              <MultiPick
                options={(dir?.groups || []).map((g) => ({ id: g.id, label: g.name }))}
                selected={p.cameras.ids}
                onChange={(ids) => setP({ ...p, cameras: { mode: 'groups', ids } })}
                placeholder="Find a camera group"
                empty="No camera groups on this site."
              />
            )}
            {p.cameras.mode === 'cameras' && (
              <MultiPick
                options={(dir?.cameras || []).map((c) => ({ id: c.id, label: c.name }))}
                selected={p.cameras.ids}
                onChange={(ids) => setP({ ...p, cameras: { mode: 'cameras', ids } })}
                placeholder="Find a camera"
                empty="No cameras on this site."
              />
            )}
          </Step>
        )}

        <Step
          n={next()}
          title="Where should it go, and who is told?"
          hint={offline ? (wantOff ? 'People only hear about sites they can see.' : undefined) : 'People only get alerts from cameras they are allowed to see.'}
        >
          {wantAi && (
            <label className="flex max-w-2xl cursor-pointer items-start gap-2.5 rounded-lg border border-border bg-muted px-3 py-2">
              <input type="checkbox" checked={p.board !== false} onChange={(e) => setP({ ...p, board: e.target.checked })} className="mt-0.5 h-4 w-4 accent-brand" />
              <span>
                <span className="flex items-center gap-1.5 text-[13px] font-semibold text-foreground"><ComputerDesktopIcon className="h-4 w-4 text-muted-foreground" /> Alert board</span>
                <span className="block text-[12px] text-muted-foreground">Show {wantSys || wantWl ? 'the AI alerts' : 'these alerts'} on the live alert board.{wantWl ? ' Watchlist hits are always shown.' : ''}</span>
              </span>
            </label>
          )}
          <PeoplePicker people={p.levels[0].people || []} onChange={(people) => setPeople(0, people)} dir={dir} names={names} onNewGroup={api.groups ? () => setNewGroupFor(0) : undefined} />
          {nobodyIn(p.levels[0]) && !escalates && (
            <Callout tone="warning">{(wantWl || (wantAi && p.board !== false)) ? 'Nobody is picked yet — the alerts only show on the alert board.' : 'Nobody is picked yet — nobody would be told.'}</Callout>
          )}
        </Step>

        <Step
          n={next()}
          title="If nobody acknowledges…"
          hint={offline ? 'Acknowledging it on the System Alerts page stops this. It also stops when the site is back online, or the problem clears.' : wantSys ? 'Acknowledging it stops this. A system problem also stops when it recovers.' : 'Acknowledging it stops this.'}
        >
          <label className="flex items-center gap-3">
            <AASwitch
              checked={escalates}
              onChange={(on) => setP((x) => (on
                ? { ...x, levels: x.levels.length > 1 ? x.levels : [...x.levels, emptyPolicyLevel(5)] }
                : { ...x, levels: x.levels.slice(0, 1), reminder: null }))}
              label="Tell more people if nobody acknowledges"
            />
            <span className="text-[13px] text-foreground">{escalates ? 'Yes, tell more people' : 'No — only the people above'}</span>
          </label>
          {escalates && (
            <div className="space-y-3">
              {p.levels.slice(1).map((l, k) => {
                const i = k + 1;
                return (
                  <div key={i} className="space-y-2 rounded-xl border border-amber-500 p-3">
                    <div className="flex flex-wrap items-center gap-1.5 text-[13px] font-medium text-foreground">
                      If still nobody after
                      <input
                        type="number" min={1} max={1440} value={l.after_min}
                        onChange={(e) => setAfter(i, Number(e.target.value))}
                        className={numCls} aria-label={`Minutes for step ${i + 1}`}
                      />
                      minutes, also tell:
                      <span className="flex-1" />
                      {!(l.people || []).length && (p.levels[0].people || []).length > 0 && (
                        <button type="button" onClick={() => setPeople(i, p.levels[0].people)}
                          className="inline-flex items-center gap-1 rounded-lg border border-border px-2 py-0.5 text-[12px] font-normal text-foreground hover:bg-muted">
                          <DocumentDuplicateIcon className="h-3.5 w-3.5" /> Same people as at first
                        </button>
                      )}
                      <button type="button" onClick={() => setP((x) => ({ ...x, levels: x.levels.filter((_, j) => j !== i) }))}
                        className="rounded-lg p-1 text-muted-foreground hover:bg-muted hover:text-destructive" aria-label={`Remove step ${i + 1}`}>
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                    <PeoplePicker people={l.people || []} onChange={(people) => setPeople(i, people)} dir={dir} names={names} onNewGroup={api.groups ? () => setNewGroupFor(i) : undefined} />
                  </div>
                );
              })}
              {p.levels.length < MAX_LEVELS && (
                <button
                  type="button"
                  onClick={() => setP((x) => ({ ...x, levels: [...x.levels, emptyPolicyLevel(x.levels[x.levels.length - 1].after_min + 10)] }))}
                  className="inline-flex items-center gap-1 text-[12px] font-medium text-brand hover:underline"
                >
                  <PlusIcon className="h-4 w-4" /> Then, if still nobody, tell someone else
                </button>
              )}
              <div className="flex flex-wrap items-center gap-2 text-[13px] text-foreground">
                <label className="flex items-center gap-1.5">
                  <input type="checkbox" checked={!!p.reminder} onChange={(e) => setP({ ...p, reminder: e.target.checked ? { every_min: 5, max: 3 } : null })} className="accent-brand" />
                  Keep reminding the last people every
                </label>
                <input type="number" min={1} max={1440} disabled={!p.reminder} value={p.reminder?.every_min ?? 5}
                  onChange={(e) => setP({ ...p, reminder: { every_min: Number(e.target.value), max: p.reminder?.max ?? 3 } })}
                  className={numCls} aria-label="Remind every (minutes)" />
                <span>minutes, up to</span>
                <input type="number" min={1} max={10} disabled={!p.reminder} value={p.reminder?.max ?? 3}
                  onChange={(e) => setP({ ...p, reminder: { every_min: p.reminder?.every_min ?? 5, max: Number(e.target.value) } })}
                  className={numCls} aria-label="How many reminders" />
                <span>times</span>
              </div>
            </div>
          )}
        </Step>

        <section className="space-y-2 rounded-xl border border-border p-4">
          <h4 className="text-[14px] font-semibold text-foreground">How it will work</h4>
          <p className="text-[12px] text-muted-foreground">Drawn from your answers above — it changes as you edit.</p>
          <EscalationTree p={p} names={names} eventTypeLabel={eventTypeLabel} />
        </section>
      </div>
      {api.groups && (
        <GroupEditor
          open={newGroupFor !== null}
          initial={null}
          dir={dir}
          names={names}
          api={api.groups}
          onClose={() => setNewGroupFor(null)}
          onSaved={(g) => {
            const at = newGroupFor ?? 0;
            setP((x) => ({ ...x, levels: x.levels.map((l, j) => (j === at ? { ...l, people: [...(l.people || []), { kind: 'group', id: g.id }] } : l)) }));
            setNewGroupFor(null);
            onGroupsChanged();
          }}
        />
      )}
    </Modal>
  );
}

// ─── Page sections ──────────────────────────────────────────────────

/** One section of the Routing page — a shaded header band (title, one line, buttons) over its content. */
function RoutingSection({ id, icon: Icon, title, purpose, actions, children }: {
  id: string; icon: typeof UsersIcon; title: string; purpose: ReactNode; actions?: ReactNode; children: ReactNode;
}) {
  return (
    <section id={id} className="scroll-mt-4 overflow-hidden rounded-xl border border-border bg-card">
      <header className="flex flex-wrap items-center gap-3 border-b border-border bg-muted px-4 py-3">
        <Icon className="h-5 w-5 shrink-0 text-muted-foreground" />
        <div className="min-w-[12rem] flex-1">
          <h3 className="text-[15px] font-semibold text-foreground">{title}</h3>
          <p className="text-[12px] text-muted-foreground">{purpose}</p>
        </div>
        {actions && <div className="flex basis-full flex-wrap items-center justify-end gap-1.5 sm:basis-auto">{actions}</div>}
      </header>
      <div className="px-4 py-3">{children}</div>
    </section>
  );
}

// ─── Contact groups ─────────────────────────────────────────────────

function GroupEditor({ open, initial, dir, names, api, onClose, onSaved }: {
  open: boolean; initial: ContactGroup | null; dir: PolicyDirectory | null; names: Names;
  api: NonNullable<RoutingPoliciesApi['groups']>; onClose: () => void; onSaved: (g: ContactGroup) => void;
}) {
  const [name, setName] = useState('');
  const [members, setMembers] = useState<PolicyPerson[]>([]);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (!open) return;
    setName(initial?.name || '');
    setMembers(initial?.members || []);
    setError('');
  }, [open, initial]);

  const save = async () => {
    if (!name.trim()) return setError('Give the group a name — e.g. "Safety team"');
    const clean = cleanPeople(members);
    if (!clean.length) return setError('Add at least one person');
    if (noWay(clean)) return setError('Someone has no way picked — tick Pop-up, Email or WhatsApp for each person.');
    setSaving(true);
    setError('');
    try {
      onSaved(await api.save(initial?.id || null, { name: name.trim(), members: clean }));
    } catch (err) {
      setError(alertActionsErrorText(err, 'Could not save the group'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      size="lg"
      title={initial ? 'Edit contact group' : 'New contact group'}
      subtitle="Each member is told the way you set here, wherever the group is picked."
      footer={(
        <div className="flex flex-wrap items-center gap-2">
          <span className="min-w-0 flex-1 text-[12px] text-destructive">{error}</span>
          <button type="button" onClick={onClose} className="rounded-lg border border-border px-3 py-1.5 text-[13px] text-foreground hover:bg-muted">Cancel</button>
          <button type="button" onClick={save} disabled={saving} className="rounded-lg bg-brand px-4 py-1.5 text-[13px] font-medium text-white hover:opacity-90 disabled:opacity-50">
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      )}
    >
      <div className="space-y-5 p-5">
        <Step n={1} title="Name">
          <input value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Safety team" className={`${aaInputCls} max-w-md`} maxLength={120} />
        </Step>
        <Step n={2} title="Who is in it, and how is each told?">
          <PeoplePicker people={members} onChange={setMembers} dir={dir} names={names} allowGroups={false} />
        </Step>
      </div>
    </Modal>
  );
}

function ContactGroups({ api, dir, names, canEdit, onChanged, policies }: {
  api: RoutingPoliciesApi; dir: PolicyDirectory | null; names: Names; canEdit: boolean; onChanged: () => void;
  /** This kind's notifications — which of them use each group. */
  policies: RoutingPolicy[];
}) {
  const [editing, setEditing] = useState<ContactGroup | null | 'new'>(null);
  const [deleting, setDeleting] = useState<ContactGroup | null>(null);
  const [error, setError] = useState('');
  const groups = dir?.contact_groups || [];
  if (!api.groups) return null;
  const groupsApi = api.groups;
  return (
    <div className="space-y-3 p-5">
      {canEdit && (
        <div className="flex justify-end">
          <button type="button" onClick={() => setEditing('new')} className="inline-flex items-center gap-1 rounded-lg bg-brand px-3 py-1.5 text-[13px] font-medium text-white hover:opacity-90">
            <PlusIcon className="h-4 w-4" /> New contact group
          </button>
        </div>
      )}
      {error && <div className="mb-2"><Callout tone="danger">{error}</Callout></div>}
      {groups.length === 0 ? (
        <p className="rounded-lg border border-dashed border-border px-3 py-3 text-center text-[13px] text-muted-foreground">No groups yet.</p>
      ) : (
        <ul className="grid grid-cols-1 gap-2 md:grid-cols-2">
          {groups.map((g) => {
            const usedBy = policies.filter((p) => p.levels.some((l) => (l.people || []).some((x) => x.kind === 'group' && x.id === g.id))).map((p) => (p.is_default ? 'Everything else' : p.name));
            return (
            <li key={g.id} className="flex items-start gap-2 rounded-lg border border-border px-3 py-2.5">
              <UsersIcon className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
              <div className="min-w-0 flex-1 space-y-1.5">
                <p className="text-[13px] font-semibold text-foreground">{g.name} <span className="font-normal text-muted-foreground">· {g.members.length} {g.members.length === 1 ? 'person' : 'people'}</span></p>
                <div className="text-[12px]"><PeopleList people={g.members} names={names} /></div>
                <p className="text-[11px] text-muted-foreground">{usedBy.length ? `Used in: ${listText(usedBy, 3)}` : 'Not used in a notification yet'}</p>
              </div>
              {canEdit && (
                <div className="flex shrink-0 gap-1">
                  <button type="button" onClick={() => setEditing(g)} className="inline-flex items-center gap-1 rounded-lg border border-border px-2.5 py-1 text-[12px] text-foreground hover:bg-muted">
                    <PencilSquareIcon className="h-4 w-4" /> Edit
                  </button>
                  <button type="button" onClick={() => setDeleting(g)} className="rounded-lg p-1.5 text-muted-foreground hover:bg-muted hover:text-destructive" title="Delete">
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>
              )}
            </li>
            );
          })}
        </ul>
      )}
      <GroupEditor
        open={editing !== null}
        initial={editing === 'new' ? null : editing}
        dir={dir}
        names={names}
        api={groupsApi}
        onClose={() => setEditing(null)}
        onSaved={() => { setEditing(null); onChanged(); }}
      />
      <ConfirmDialog
        open={!!deleting}
        title="Delete this contact group?"
        message={deleting ? `"${deleting.name}" will be deleted. A group still used by a notification cannot be deleted — take it out of the notification first.` : ''}
        confirmLabel="Delete"
        variant="danger"
        onCancel={() => setDeleting(null)}
        onConfirm={async () => {
          if (!deleting) return;
          try {
            await groupsApi.remove(deleting.id);
            setError('');
            onChanged();
          } catch (err) {
            setError(alertActionsErrorText(err, 'Could not delete the group'));
          }
          setDeleting(null);
        }}
      />
    </div>
  );
}

// ─── "Who would be told?" ───────────────────────────────────────────

function WhoGetsThis({ api, dir, eventTypeLabel, variant = 'alerts' }: { api: RoutingPoliciesApi; dir: PolicyDirectory | null; eventTypeLabel: (t: string) => string; variant?: 'alerts' | 'offline' }) {
  const [kind, setKind] = useState<PolicyKind>(variant === 'offline' ? 'system' : 'ai');
  const offline = variant === 'offline';
  // Cloud (platform): a site going offline, or a cloud server problem.
  const [cloudSample, setCloudSample] = useState(false);
  // "and it is: critical" shows by default — send it too, or a critical-only card never matches.
  const [sample, setSample] = useState<Record<string, string>>({ severity: 'critical' });
  const [result, setResult] = useState<PolicyPreview | null>(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  useEffect(() => { setResult(null); setSample({ severity: 'critical' }); }, [kind]);

  const run = async () => {
    setBusy(true);
    setError('');
    try {
      setResult(await api.preview(kind, sample));
    } catch (err) {
      setError(alertActionsErrorText(err, 'Could not work it out'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <RoutingSection
      id="routing-check"
      icon={ShieldCheckIcon}
      title="Check who would be told"
      purpose={offline ? 'Try an example — nothing is sent.' : 'Try an example alert — nothing is sent.'}
    >
      {!offline && (
        <div className="mb-2 flex flex-wrap gap-1.5">
          <Choice on={kind === 'ai'} onClick={() => setKind('ai')}>An AI alert</Choice>
          <Choice on={kind === 'system'} onClick={() => setKind('system')}>A system problem</Choice>
        </div>
      )}
      {offline && (
        <div className="mb-2 flex flex-wrap gap-1.5">
          <Choice on={!cloudSample} onClick={() => { setCloudSample(false); setSample({}); setResult(null); }}>A site goes offline</Choice>
          <Choice on={cloudSample} onClick={() => { setCloudSample(true); setSample({ cloud_kind: 'cpu' }); setResult(null); }}>A cloud server problem</Choice>
        </div>
      )}
      <div className="flex flex-wrap items-end gap-2">
        {offline && cloudSample ? (
          <label className="min-w-[12rem] flex-1">
            <span className={aaLabelCls}>If this happens on the cloud server</span>
            <select value={sample.cloud_kind || 'cpu'} onChange={(e) => setSample({ cloud_kind: e.target.value })} className={aaInputCls}>
              {CLOUD_PROBLEMS.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
            </select>
          </label>
        ) : offline ? (
          <label className="min-w-[12rem] flex-1">
            <span className={aaLabelCls}>If this site goes offline</span>
            <select value={sample.node_id || ''} onChange={(e) => setSample({ node_id: e.target.value })} className={aaInputCls}>
              <option value="">Pick a site</option>
              {(dir?.cameras || []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
            </select>
          </label>
        ) : kind === 'ai' ? (
          <>
            <label className="min-w-[12rem] flex-1">
              <span className={aaLabelCls}>If this camera</span>
              <select value={sample.camera_id || ''} onChange={(e) => setSample({ ...sample, camera_id: e.target.value })} className={aaInputCls}>
                <option value="">Pick a camera</option>
                {(dir?.cameras || []).map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </label>
            <label className="min-w-[10rem] flex-1">
              <span className={aaLabelCls}>raises an alert from</span>
              <select value={sample.watchlist ? `wl:${sample.watchlist}` : sample.rule_id || ''} onChange={(e) => {
                const v = e.target.value;
                if (v.startsWith('wl:')) return setSample({ ...sample, rule_id: '', event_type: '', watchlist: v.slice(3) });
                const r = (dir?.rules || []).find((x) => x.id === v);
                setSample({ ...sample, rule_id: v, event_type: r?.event_type?.split(',')[0] || '', watchlist: '' });
              }} className={aaInputCls}>
                <option value="">any rule</option>
                <optgroup label="Alert rules">
                  {(dir?.rules || []).map((r) => <option key={r.id} value={r.id}>{r.name || eventTypeLabel(r.event_type)}</option>)}
                </optgroup>
                <optgroup label="Watchlist">
                  {WATCHLIST_ROUTING_CATEGORIES.map((c) => <option key={c.value} value={`wl:${c.value}`}>{c.label}</option>)}
                </optgroup>
              </select>
            </label>
            <label className="w-36">
              <span className={aaLabelCls}>and it is</span>
              <select value={sample.severity || 'critical'} onChange={(e) => setSample({ ...sample, severity: e.target.value })} className={aaInputCls}>
                <option value="critical">critical</option>
                <option value="warning">a warning</option>
                <option value="normal">for information</option>
              </select>
            </label>
          </>
        ) : (
          <label className="min-w-[12rem] flex-1">
            <span className={aaLabelCls}>If this happens</span>
            <select value={sample.kind || 'service'} onChange={(e) => setSample({ kind: e.target.value })} className={aaInputCls}>
              {SYSTEM_ALERT_KINDS.map((k) => <option key={k.value} value={k.value}>{k.label}</option>)}
            </select>
          </label>
        )}
        <button type="button" onClick={run} disabled={busy || (offline ? !(sample.node_id || sample.cloud_kind) : kind === 'ai' && !sample.camera_id)} className="rounded-lg bg-brand px-3 py-1.5 text-[13px] font-medium text-white hover:opacity-90 disabled:opacity-50">
          {busy ? 'Checking…' : 'Check'}
        </button>
      </div>
      {error && <p className="mt-2 text-[12px] text-destructive">{error}</p>}
      {result && (
        <div className="mt-3 space-y-2 text-[13px]">
          <p className="text-muted-foreground">
            Caught by: {result.policies.filter((x) => !(result.fallback && x.is_default)).map((x) => x.is_default ? 'Everything else' : x.name).join(', ') || 'nothing'}
          </p>
          {result.fallback && (
            <Callout tone="warning">
              Nobody on {result.fallback.policies.map((nm) => `"${nm}"`).join(', ')} can be reached right now ({result.fallback.reason}),
              so <b>Everything else</b> is told straight away instead — the alert is never lost.
            </Callout>
          )}
          {result.levels.map((l) => (
            <div key={l.level} className="rounded-lg border border-border px-3 py-2">
              <p className="mb-1 text-[12px] font-semibold text-foreground">
                {l.level === 1 ? 'Told straight away' : `If nobody acknowledges — step ${l.level}`}
              </p>
              {l.people.length === 0
                ? <p className="text-muted-foreground">Nobody.</p>
                : <p className="text-foreground">{l.people.map((x) => `${x.name} (${[x.dashboard && 'pop-up', x.email && 'email', x.phone && 'WhatsApp'].filter(Boolean).join(' + ')})`).join(', ')}</p>}
              {l.left_out.length > 0 && <p className="mt-1 text-[12px] text-muted-foreground">Not told: {l.left_out.map((x) => `${x.name} — ${x.reason}`).join('; ')}</p>}
            </div>
          ))}
        </div>
      )}
    </RoutingSection>
  );
}

// ─── The list ───────────────────────────────────────────────────────

export default function RoutingPolicies({ api, canEdit, perspective = 'site', eventTypeLabel = (t) => t, header, variant = 'alerts', toolbarTarget }: RoutingPoliciesProps) {
  const [policies, setPolicies] = useState<RoutingPolicy[] | null>(null);
  const [dir, setDir] = useState<PolicyDirectory | null>(null);
  const [error, setError] = useState('');
  const [editing, setEditing] = useState<RoutingPolicy | null | 'new'>(null);
  const [deleting, setDeleting] = useState<RoutingPolicy | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [treeFor, setTreeFor] = useState<string | null>(null);
  const [showHistory, setShowHistory] = useState(false);
  const [showGroups, setShowGroups] = useState(false);

  const loadDir = useCallback(() => api.directory().then(setDir).catch(() => {}), [api]);
  const load = useCallback(async () => {
    setError('');
    const [list, directory] = await Promise.allSettled([api.list(), api.directory()]);
    if (list.status === 'fulfilled') setPolicies(list.value);
    else setError(alertActionsErrorText(list.reason, 'Could not read who is notified'));
    if (directory.status === 'fulfilled') setDir(directory.value);
  }, [api]);
  useEffect(() => { setPolicies(null); load(); }, [load]);

  const names: Names = useMemo(() => ({
    user: new Map((dir?.users || []).map((u) => [u.id, u.name])),
    role: new Map((dir?.roles || []).map((r) => [r.id, r.name])),
    camera: new Map((dir?.cameras || []).map((c) => [c.id, c.name])),
    group: new Map((dir?.groups || []).map((g) => [g.id, g.name])),
    rule: new Map((dir?.rules || []).map((r) => [r.id, r.name || eventTypeLabel(r.event_type)])),
    contactGroup: new Map((dir?.contact_groups || []).map((g) => [g.id, g])),
  }), [dir, eventTypeLabel]);

  const toggle = async (p: RoutingPolicy, enabled: boolean) => {
    setBusyId(p.id);
    try {
      const saved = await api.save(p.id, { enabled });
      setPolicies((list) => (list || []).map((x) => (x.id === p.id ? saved : x)));
    } catch (err) {
      setError(alertActionsErrorText(err, 'Could not change it'));
    } finally {
      setBusyId(null);
    }
  };

  const ordered = useMemo(() => [...(policies || [])].sort((a, b) => Number(a.is_default) - Number(b.is_default) || a.position - b.position), [policies]);

  const custom = ordered.filter((p) => !p.is_default);
  // "Everything else" — one card (an older site has one per kind: AI first).
  const fallbacks = ordered.filter((p) => p.is_default).sort((a, b) => Number(a.kind !== 'ai') - Number(b.kind !== 'ai'));
  const card = (p: RoutingPolicy) => {
    const later = (p.levels || []).slice(1);
    const row = (k: string, v: ReactNode) => (
      <>
        <dt className="pt-0.5 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{k}</dt>
        <dd className="min-w-0 text-[13px] text-foreground">{v}</dd>
      </>
    );
    return (
      <article key={p.id} className={`rounded-xl border px-4 py-3 ${p.is_default ? 'border-dashed border-border bg-muted' : 'border-border bg-card'} ${p.enabled ? '' : 'opacity-60'}`}>
        <div className="flex flex-wrap items-center gap-3">
          <AASwitch checked={p.enabled} onChange={(v) => toggle(p, v)} disabled={!canEdit} busy={busyId === p.id} label={p.enabled ? 'Turn off' : 'Turn on'} />
          <p className="min-w-[10rem] flex-1 text-[14px] font-semibold text-foreground">
            {p.is_default ? <>Everything else{defaultSuffix(p) && <span className="font-normal text-muted-foreground"> — {defaultSuffix(p)}</span>}</> : p.name}
            {!p.enabled && <span className="ml-2 text-[12px] font-normal text-muted-foreground">(off — not used)</span>}
          </p>
          {canEdit && (
            <div className="flex items-center gap-1">
              <button type="button" onClick={() => setEditing(p)} className="inline-flex items-center gap-1 rounded-lg border border-border bg-card px-2.5 py-1 text-[12px] text-foreground hover:bg-muted">
                <PencilSquareIcon className="h-4 w-4" /> Edit
              </button>
              {!p.is_default && (
                <button type="button" onClick={() => setDeleting(p)} className="rounded-lg p-1.5 text-muted-foreground hover:bg-muted hover:text-destructive" title="Delete">
                  <TrashIcon className="h-4 w-4" />
                </button>
              )}
            </div>
          )}
        </div>
        <dl className="mt-2.5 grid grid-cols-1 gap-x-4 gap-y-1.5 sm:grid-cols-[10rem_minmax(0,1fr)]">
          {isOffline(p)
            ? row('Which alerts', p.is_default ? `Any ${defaultWhat(p)} no other card catches` : <CloudLines p={p} names={names} />)
            : row('Which alerts', <AlertsLines p={p} names={names} eventTypeLabel={eventTypeLabel} />)}
          {coversAi(p) && !p.is_default && row(coversSystem(p) ? 'Cameras (AI)' : 'Cameras', camerasText(p, names))}
          {coversAi(p) && row('Alert board', !pickedAi(p)
            ? 'Always shown (watchlist hits)'
            : <>{p.board !== false ? 'Shown on the live alert board' : <span className="text-muted-foreground">Not shown</span>}{pickedWatch(p) && <span className="text-muted-foreground"> · watchlist hits always</span>}</>)}
          {row('Who is told', <PeopleList people={p.levels?.[0]?.people || []} names={names} />)}
          {row('If nobody acknowledges', later.length || p.reminder ? (
            <ul className="space-y-1">
              {later.map((l, i) => (
                <li key={i}>
                  <span className="text-muted-foreground">after {l.after_min} min, also tell</span>
                  <div className="mt-0.5 pl-3"><PeopleList people={l.people || []} names={names} /></div>
                </li>
              ))}
              {p.reminder && <li className="text-muted-foreground">then remind them every {p.reminder.every_min} min, up to {p.reminder.max} times</li>}
            </ul>
          ) : <span className="text-muted-foreground">Nobody else is told</span>)}
        </dl>
        <button type="button" onClick={() => setTreeFor(treeFor === p.id ? null : p.id)} className="mt-2 text-[12px] font-medium text-brand hover:underline">
          {treeFor === p.id ? 'Hide the picture' : 'See it as a picture'}
        </button>
        {treeFor === p.id && <div className="mt-2"><EscalationTree p={p} names={names} eventTypeLabel={eventTypeLabel} /></div>}
      </article>
    );
  };

  const hasGroups = !!api.groups;

  const tools = (hasGroups || api.history) && (
    <>
        {hasGroups && (
          <button type="button" onClick={() => setShowGroups(true)} className="inline-flex items-center gap-1 rounded-lg border border-border bg-card px-2.5 py-1.5 text-[12px] text-foreground hover:bg-muted" title="Saved lists of people you notify together">
            <UsersIcon className="h-4 w-4" /> Contact groups{(dir?.contact_groups || []).length ? ` (${(dir?.contact_groups || []).length})` : ''}
          </button>
        )}
        {api.history && (
          <button type="button" onClick={() => setShowHistory(true)} className="inline-flex items-center gap-1 rounded-lg border border-border bg-card px-2.5 py-1.5 text-[12px] text-foreground hover:bg-muted" title="Who changed routing, and when">
            <ClockIcon className="h-4 w-4" /> History
          </button>
        )}
    </>
  );

  return (
    <div className="space-y-5">
      {header}
      {toolbarTarget ? tools && createPortal(tools, toolbarTarget) : tools && <div className="flex flex-wrap items-center justify-end gap-2">{tools}</div>}
      {error && <Callout tone="danger">{error}</Callout>}

      <RoutingSection
        id="routing-notify"
        icon={BellAlertIcon}
        title="Who is told"
        purpose={variant === 'offline' ? 'Sites going offline and the cloud server’s own problems — who hears, and how.' : 'Which AI alerts and system problems go to whom, and how.'}
        actions={<>
          <button type="button" onClick={load} className="rounded-lg border border-border bg-card p-1.5 text-muted-foreground hover:bg-muted" title="Reload">
            <ArrowPathIcon className="h-4 w-4" />
          </button>
          {canEdit && (
            <button type="button" onClick={() => setEditing('new')} className="inline-flex items-center gap-1 rounded-lg bg-brand px-3 py-1.5 text-[13px] font-medium text-white hover:opacity-90">
              <PlusIcon className="h-4 w-4" /> Add who to notify
            </button>
          )}
        </>}
      >
        <div className="space-y-3">
          {policies === null && <p className="text-[13px] text-muted-foreground">Loading…</p>}
          {policies !== null && custom.length === 0 && (
            <p className="rounded-lg border border-dashed border-border px-3 py-4 text-center text-[13px] text-muted-foreground">
              No notifications yet — everything goes to <b className="text-foreground">Everything else</b>.
            </p>
          )}
          {custom.length > 0 && <div className="space-y-2">{custom.map(card)}</div>}
          {fallbacks.length > 0 && (
            <div className="space-y-1.5">
              <p className="text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">When no card above catches it</p>
              <div className="space-y-2">{fallbacks.map(card)}</div>
            </div>
          )}
        </div>
      </RoutingSection>

      {hasGroups && (
        <Modal open={showGroups} onClose={() => setShowGroups(false)} size="xl" title="Contact groups"
          subtitle="Saved lists of people — pick a group in any notification.">
          <ContactGroups api={api} dir={dir} names={names} canEdit={canEdit} onChanged={loadDir} policies={policies || []} />
        </Modal>
      )}

      <WhoGetsThis api={api} dir={dir} eventTypeLabel={eventTypeLabel} variant={variant} />

      {api.history && (
        <Modal open={showHistory} onClose={() => setShowHistory(false)} size="lg" title="Change history" subtitle="Who changed a notification, a contact group or a pause, and when.">
          <div className="p-5"><ChangeHistory load={api.history} title="Changes" bare defaultOpen /></div>
        </Modal>
      )}

      <PolicyEditor
        open={editing !== null}
        initial={editing === 'new' ? null : editing}
        variant={variant}
        dir={dir}
        names={names}
        api={api}
        eventTypeLabel={eventTypeLabel}
        onClose={() => setEditing(null)}
        onSaved={(saved) => {
          setPolicies((list) => [...(list || []).filter((x) => x.id !== saved.id), saved]);
          setEditing(null);
        }}
        onGroupsChanged={loadDir}
      />
      <ConfirmDialog
        open={!!deleting}
        title="Delete this notification?"
        message={deleting ? `"${deleting.name}" will stop telling its people. Its alerts go to the other cards, or to "Everything else".` : ''}
        confirmLabel="Delete"
        variant="danger"
        onCancel={() => setDeleting(null)}
        onConfirm={async () => {
          if (!deleting) return;
          try {
            await api.remove(deleting.id);
            setPolicies((list) => (list || []).filter((x) => x.id !== deleting.id));
          } catch (err) {
            setError(alertActionsErrorText(err, 'Could not delete it'));
          }
          setDeleting(null);
        }}
      />
    </div>
  );
}
